import ArticleCard from "@components/Blocks/Card/Article/ArticleCard";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import VerticalStoryPackage from "@components/Layouts/HomePageStoryPackages/VerticalStoryPackage";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import LoadMore from "@components/Modules/LoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@components/Modules/LoadMore/SetFirstRenderedArticleIds";
import { SECTION_INITIAL_ARTICLE_COUNT } from "@config/constants";
import { LOAD_MORE_CTA_COPY } from "@config/ctaCopy";
import { AdsLb2 } from "@elements/Advertisement/variant/AdsLb";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import fetchBHApi from "@helper/fetchBHApi";
import { displayAfter } from "@helper/utils";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import type { ProcessedArticleData } from "@transformer/useOSResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import { ReactNode, useState } from "react";

const PAGE_SIZE = 8;

type SectionArticlesListNewProps = {
  sectionArticles: ProcessedArticleData[];
  sectionName: string | undefined;
  adsSectionName: string | undefined;
  paywall: LabelCardIconVariety | null;
  isTerkini: boolean;
};

export default function SectionArticlesListNew({
  sectionArticles,
  sectionName,
  adsSectionName,
  paywall,
  isTerkini,
}: SectionArticlesListNewProps) {
  const headerArticle = [...sectionArticles.slice(0, 4)];
  const onlineStories = [...sectionArticles.slice(4)];
  const contentAccess = headerArticle[0].contentAccess;

  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const showLoadMore: boolean =
    sectionArticles.length == SECTION_INITIAL_ARTICLE_COUNT;

  /**
   * Sets a precaution for duplicate article ids when rendering additional articles
   */
  useSetFirstRenderedArticleIds(sectionArticles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length == 0 ? sectionArticles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: paywall !== null ? contentAccess : sectionName,
      sort: lastLoadedArticle && lastLoadedArticle.sort,
    };

    const fetchMoreTerkiniStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[]> => {
      const queryParams = new URLSearchParams({
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "8",
      }).toString();
      const response: ProcessedArticleData[] = await fetchBHApi(
        `breaking-news?${queryParams}`,
        "GET",
      );
      return response;
    };

    const fetchMorePaywallStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[]> => {
      const queryParams = new URLSearchParams({
        contentAccess: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "8",
      }).toString();
      const response: ProcessedArticleData[] = await fetchBHApi(
        `more-section-paywall-stories?${queryParams}`,
        "GET",
      );
      return response;
    };

    const fetchMoreStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      // Construct the query string
      const queryParams = new URLSearchParams({
        section: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: "8",
      }).toString();
      const response: ProcessedArticleData[] = await fetchBHApi(
        `more-section-stories?${queryParams}`,
        "GET",
      );
      return response;
    };

    const fetchFunction: FetchMoreArticlesFunction = isTerkini
      ? fetchMoreTerkiniStoriesData
      : paywall !== null
        ? fetchMorePaywallStoriesData
        : fetchMoreStoriesData;

    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: PAGE_SIZE,
      pageNumber,
    };
    const response = await CreateLoadMoreHandler(createLoadMoreProps);
    return response;
  };

  return (
    <>
      <div className="w-full">
        <WidgetWrapper>
          <VerticalStoryPackage
            firstCardVariant={paywall !== null || isTerkini ? "xl" : "xxl"}
            restCardVariant={paywall !== null || isTerkini ? "medium" : "large"}
            renderItems={headerArticle}
            styles={{
              wrapperStyle: "border-t-0",
            }}
            isSectionPage={true}
          />
        </WidgetWrapper>
        <AdsContainer>
          <AdsLb2
            uniqueSectionName={adsSectionName}
            pageAdTargetType={PageAdTargetingTypeEnum.LISTING}
            hasStripeBg={false}
          />
        </AdsContainer>
        <div className="w-full">
          {[...onlineStories, ...loadMoreData].reduce(
            (acc: ReactNode[], _, index) => {
              // Insert an ad every PAGE_SIZE articles, outside the wrapper
              if (displayAfter(index, PAGE_SIZE)) {
                acc.push(
                  <AdsContainer key={`ad-${index}`}>
                    <CompanionAdsLb
                      uniqueSectionName={adsSectionName}
                      index={index / PAGE_SIZE}
                      pageAdTargetType={PageAdTargetingTypeEnum.LISTING}
                      stripeBackground={false}
                      overRideSlot={false}
                      applySizeMapping={true}
                    />
                  </AdsContainer>,
                );
              }
              // Start a new batch every 8 articles
              if (index % PAGE_SIZE === 0) {
                acc.push(
                  <WidgetWrapper key={`batch-${index}`} className="my-[20px]">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {[...onlineStories, ...loadMoreData]
                        .slice(index, index + PAGE_SIZE)
                        .map((item, idx) => (
                          <ArticleCard
                            key={`batch-${index}-${idx}`}
                            {...item}
                            orientation="horizontal"
                            variant={
                              paywall !== null || isTerkini ? "medium" : "large"
                            }
                            forNewDesign={true}
                            displayWidth={160}
                            displayHeight={120}
                            displayAuthor={true}
                          />
                        ))}
                    </div>
                  </WidgetWrapper>,
                );
              }

              return acc;
            },
            [],
          )}

          {showLoadMore ? (
            <LoadMore
              rootClassName="my-4"
              onLoadMore={handleLoadMore}
              hasMore={hasMoreStories}
              loadText={LOAD_MORE_CTA_COPY}
              virtualPVTitle={sectionName || ""}
              pageNumber={pageNumber}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}
